/**
 * WHAT: Custom SQL Executor dashboard showing summary statistics and recent activity.
 * WHY: Organizations using their own UAR SQL scripts need visibility into template usage,
 *      job execution status, and data extraction volumes to ensure timely access reviews.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api.config';

/**
 * WHAT: Dashboard overview for the Custom SQL Executor module.
 * WHY: Gives DBAs a summary of templates, jobs, executions, and data extraction
 *      metrics so they can monitor SQL-based UAR data collection at a glance.
 */
const CustomDashboard = ({ onNavigate }) => {
  const [summary, setSummary] = useState(null);
  const [techStats, setTechStats] = useState([]);
  const [recentExecutions, setRecentExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, techRes, execRes] = await Promise.all([
        axios.get(`${API_CONFIG.BASE_URL}/api/custom/dashboard/summary/`),
        axios.get(`${API_CONFIG.BASE_URL}/api/custom/dashboard/technology-stats/`),
        axios.get(`${API_CONFIG.BASE_URL}/api/custom/executions/?limit=10`),
      ]);
      setSummary(summaryRes.data);
      setTechStats(techRes.data.stats || []);
      setRecentExecutions(execRes.data.executions || []);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      COMPLETED: 'bg-success',
      RUNNING: 'bg-primary',
      PENDING: 'bg-warning text-dark',
      FAILED: 'bg-danger',
      PARTIAL: 'bg-warning text-dark',
      QUEUED: 'bg-info',
    };
    return badges[status] || 'bg-secondary';
  };

  const getTechnologyBadge = (tech) => {
    const badges = {
      ORACLE: 'bg-danger',
      MSSQL: 'bg-primary',
      POSTGRESQL: 'bg-info',
      MYSQL: 'bg-warning text-dark',
      CASSANDRA: 'bg-success',
    };
    return badges[tech] || 'bg-secondary';
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
        <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchDashboardData}>
          <i className="fas fa-sync me-1"></i> Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-3 mb-3">
          <div className="card bg-primary text-white h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 opacity-75">Active Templates</h6>
                  <h2 className="card-title mb-0">{summary?.active_templates || 0}</h2>
                </div>
                <i className="fas fa-file-code fa-2x opacity-50"></i>
              </div>
            </div>
            <div className="card-footer bg-transparent border-0">
              <button
                className="btn btn-sm btn-outline-light"
                onClick={() => onNavigate('templates')}
              >
                View Templates <i className="fas fa-arrow-right ms-1"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="card bg-success text-white h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 opacity-75">Active Jobs</h6>
                  <h2 className="card-title mb-0">{summary?.active_jobs || 0}</h2>
                </div>
                <i className="fas fa-tasks fa-2x opacity-50"></i>
              </div>
              <small className="opacity-75">
                {summary?.scheduled_jobs || 0} scheduled
              </small>
            </div>
            <div className="card-footer bg-transparent border-0">
              <button
                className="btn btn-sm btn-outline-light"
                onClick={() => onNavigate('jobs')}
              >
                View Jobs <i className="fas fa-arrow-right ms-1"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="card bg-info text-white h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 opacity-75">Executions Today</h6>
                  <h2 className="card-title mb-0">{summary?.executions_today || 0}</h2>
                </div>
                <i className="fas fa-play-circle fa-2x opacity-50"></i>
              </div>
              <small className="opacity-75">
                {summary?.running_executions || 0} running now
              </small>
            </div>
            <div className="card-footer bg-transparent border-0">
              <button
                className="btn btn-sm btn-outline-light"
                onClick={() => onNavigate('executions')}
              >
                View Executions <i className="fas fa-arrow-right ms-1"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="card bg-warning text-dark h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 opacity-75">Pending Approvals</h6>
                  <h2 className="card-title mb-0">{summary?.pending_approvals || 0}</h2>
                </div>
                <i className="fas fa-clock fa-2x opacity-50"></i>
              </div>
              <small className="opacity-75">
                {summary?.failures_today || 0} failures today
              </small>
            </div>
            <div className="card-footer bg-transparent border-0">
              <button
                className="btn btn-sm btn-outline-dark"
                onClick={() => onNavigate('templates')}
              >
                Review <i className="fas fa-arrow-right ms-1"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Rows Extracted Today */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Total Rows Extracted Today</h6>
                  <h3 className="mb-0">
                    {(summary?.rows_extracted_today || 0).toLocaleString()}
                  </h3>
                </div>
                <div className="text-end text-muted">
                  <small>Last execution:</small>
                  <br />
                  <small>
                    {summary?.last_execution_at
                      ? new Date(summary.last_execution_at).toLocaleString()
                      : 'Never'}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Technology Stats */}
        <div className="col-md-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-secondary text-white">
              <h5 className="mb-0">
                <i className="fas fa-database me-2"></i>
                Statistics by Technology
              </h5>
            </div>
            <div className="card-body p-0">
              {techStats.length > 0 ? (
                <table className="table table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Technology</th>
                      <th className="text-center">Templates</th>
                      <th className="text-center">Jobs</th>
                      <th className="text-center">Executions</th>
                      <th className="text-end">Rows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {techStats.map((stat, idx) => (
                      <tr key={idx}>
                        <td>
                          <span className={`badge ${getTechnologyBadge(stat.technology)}`}>
                            {stat.technology}
                          </span>
                        </td>
                        <td className="text-center">{stat.template_count}</td>
                        <td className="text-center">{stat.active_job_count}</td>
                        <td className="text-center">{stat.total_executions}</td>
                        <td className="text-end">
                          {(stat.total_rows_extracted || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="fas fa-chart-bar fa-3x mb-3 opacity-25"></i>
                  <p>No data available yet</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Executions */}
        <div className="col-md-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-history me-2"></i>
                Recent Executions
              </h5>
              <button
                className="btn btn-sm btn-outline-light"
                onClick={() => onNavigate('executions')}
              >
                View All
              </button>
            </div>
            <div className="card-body p-0">
              {recentExecutions.length > 0 ? (
                <table className="table table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Template</th>
                      <th className="text-center">Status</th>
                      <th className="text-center">Connections</th>
                      <th className="text-end">Rows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentExecutions.slice(0, 8).map((exec) => (
                      <tr key={exec.execution_id}>
                        <td>
                          <small className="text-muted">{exec.template_code}</small>
                          <br />
                          <small>{exec.template_name}</small>
                        </td>
                        <td className="text-center">
                          <span className={`badge ${getStatusBadge(exec.status)}`}>
                            {exec.status}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="text-success">{exec.successful_connections}</span>
                          {exec.failed_connections > 0 && (
                            <span className="text-danger ms-1">
                              / {exec.failed_connections}
                            </span>
                          )}
                        </td>
                        <td className="text-end">
                          {(exec.total_rows_extracted || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="fas fa-play-circle fa-3x mb-3 opacity-25"></i>
                  <p>No executions yet</p>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => onNavigate('jobs')}
                  >
                    <i className="fas fa-plus me-1"></i>
                    Create Your First Job
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-light">
              <h5 className="mb-0">
                <i className="fas fa-bolt me-2"></i>
                Quick Actions
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 mb-2">
                  <button
                    className="btn btn-outline-primary w-100"
                    onClick={() => onNavigate('templates')}
                  >
                    <i className="fas fa-plus me-2"></i>
                    New Template
                  </button>
                </div>
                <div className="col-md-3 mb-2">
                  <button
                    className="btn btn-outline-success w-100"
                    onClick={() => onNavigate('jobs')}
                  >
                    <i className="fas fa-plus me-2"></i>
                    New Job
                  </button>
                </div>
                <div className="col-md-3 mb-2">
                  <button
                    className="btn btn-outline-info w-100"
                    onClick={() => onNavigate('executions')}
                  >
                    <i className="fas fa-search me-2"></i>
                    View Results
                  </button>
                </div>
                <div className="col-md-3 mb-2">
                  <button
                    className="btn btn-outline-secondary w-100"
                    onClick={() => onNavigate('settings')}
                  >
                    <i className="fas fa-cog me-2"></i>
                    Settings
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomDashboard;
